import React, { useState, memo, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  Add,
  FolderOpenOutlined,
  MoreHoriz,
  Edit,
  Delete,
  Search,
  Close,
  Business,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { formatDate } from "../utils/dateUtils";
import { projectPath } from "../utils/navigation";
import {
  PageContainer,
  PageHeader,
  SectionCard,
  EmptyState,
  ListSkeleton,
  ConfirmDialog,
} from "./pagekit";

/**
 * One count in a project card's pipeline line.
 * @param {object} props
 * @param {number} props.value - Count.
 * @param {string} props.label - Label.
 * @returns {JSX.Element}
 */
function PipelineCount({ value, label }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" component="div" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" noWrap>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Projects page: every project the user can see, with search, a customer
 * filter and create / edit / delete.
 * @returns {JSX.Element}
 */
const ProjectList = memo(() => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { state: authState } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    customer_id: "",
  });

  const customersById = useMemo(() => {
    const map = new Map();
    (state.customers || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [state.customers]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (state.projects || []).filter((project) => {
      if (customerFilter && project.customer_id !== customerFilter) return false;
      if (!q) return true;
      const customerName = customersById.get(project.customer_id)?.name || project.customer?.name || "";
      return (
        project.name?.toLowerCase().includes(q) ||
        project.description?.toLowerCase().includes(q) ||
        customerName.toLowerCase().includes(q)
      );
    });
  }, [state.projects, search, customerFilter, customersById]);

  const openCreateDialog = useCallback(() => {
    setFormData({ name: "", description: "", customer_id: customerFilter || (state.customers?.length === 1 ? state.customers[0].id : "") });
    setCreateDialogOpen(true);
  }, [customerFilter, state.customers]);

  const handleCreateProject = useCallback(() => {
    if (formData.name.trim() && formData.customer_id) {
      actions.createProject(formData);
      setFormData({ name: "", description: "", customer_id: "" });
      setCreateDialogOpen(false);
    }
  }, [actions, formData]);

  const handleEditProject = useCallback(() => {
    if (formData.name.trim() && projectToEdit) {
      actions.updateProject(projectToEdit.id, {
        name: formData.name,
        description: formData.description,
      });
      setFormData({ name: "", description: "", customer_id: "" });
      setEditDialogOpen(false);
      setProjectToEdit(null);
    }
  }, [actions, formData, projectToEdit]);

  const handleConfirmDelete = useCallback(() => {
    if (projectToDelete) actions.deleteProject(projectToDelete.id);
    setProjectToDelete(null);
  }, [actions, projectToDelete]);

  const openEditDialog = useCallback((project) => {
    setProjectToEdit(project);
    setFormData({
      name: project.name || "",
      description: project.description || "",
    });
    setEditDialogOpen(true);
    setMenuAnchor(null);
    setSelectedProject(null);
  }, []);

  const handleMenuOpen = useCallback((event, project) => {
    setMenuAnchor(event.currentTarget);
    setSelectedProject(project);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedProject(null);
  }, []);

  const loading = state.loading || (!state.initialLoadComplete && authState.loading);
  const hasProjects = (state.projects || []).length > 0;
  const hasCustomers = (state.customers || []).length > 0;

  let body;
  if (loading) {
    body = <ListSkeleton variant="cards" rows={6} label="Loading projects" />;
  } else if (!state.initialLoadComplete) {
    body = null;
  } else if (!hasProjects) {
    body = (
      <SectionCard>
        <EmptyState
          inky
          title="No projects yet"
          description={hasCustomers
            ? "A project collects the meetings, requirements and documents for one piece of work."
            : "Projects belong to a customer. Add your first customer, then create a project for it."}
          actions={hasCustomers ? (
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              New project
            </Button>
          ) : (
            <Button variant="contained" startIcon={<Business />} onClick={() => navigate("/customers")}>
              Add a customer
            </Button>
          )}
        />
      </SectionCard>
    );
  } else if (!filteredProjects.length) {
    body = (
      <SectionCard>
        <EmptyState
          compact
          icon={<Search />}
          title="No projects match"
          description="Try a different search or customer."
          actions={<Button variant="outlined" onClick={() => { setSearch(""); setCustomerFilter(""); }}>Clear filters</Button>}
        />
      </SectionCard>
    );
  } else {
    body = (
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
          gap: 2,
        }}
      >
        {filteredProjects.map((project) => {
          const customer = customersById.get(project.customer_id) || project.customer || null;
          return (
            <Box
              component="li"
              key={project.id}
              sx={{
                listStyle: "none",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 2.25,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: "var(--q-radius-card, 16px)",
                transition: "border-color 120ms ease, box-shadow 120ms ease",
                "&:hover": { borderColor: "var(--q-border-hover)", boxShadow: "var(--q-shadow-md)" },
                "&:focus-within": { borderColor: "primary.main" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {customer && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {customer.name}
                    </Typography>
                  )}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(projectPath(project.id))}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      display: "block",
                      maxWidth: "100%",
                      borderRadius: 1,
                      // Whole card is the click target for pointer users.
                      "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "inherit" },
                      "&:focus-visible": { outline: "none" },
                    }}
                  >
                    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }} noWrap>
                      {project.name}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Actions for ${project.name}`}
                  onClick={(e) => handleMenuOpen(e, project)}
                  sx={{ position: "relative", zIndex: 1, mt: -0.5, mr: -0.5 }}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: "2.9em" }}
              >
                {project.description || "No description"}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                  pt: 1.5,
                  borderTop: 1,
                  borderColor: "var(--q-border-subtle)",
                }}
              >
                <PipelineCount value={project.sessions?.length || 0} label="Sessions" />
                <PipelineCount value={project.requirements?.length || 0} label="Requirements" />
                <PipelineCount value={project.generatedDocuments?.length || 0} label="Documents" />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Created {formatDate(project.createdAt)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Projects"
        description="Each project turns meetings into requirements and documents."
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} disabled={!hasCustomers && !loading}>
            New project
          </Button>
        }
      />

      {hasProjects && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects"
            inputProps={{ "aria-label": "Search projects" }}
            sx={{ flex: "1 1 260px", maxWidth: 420, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {hasCustomers && (
            <FormControl sx={{ minWidth: 200, bgcolor: "background.paper", borderRadius: "var(--q-radius-md)" }}>
              <InputLabel id="project-customer-filter">Customer</InputLabel>
              <Select
                labelId="project-customer-filter"
                label="Customer"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {state.customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {(search || customerFilter) && (
            <Chip label={`${filteredProjects.length} of ${state.projects.length}`} size="small" />
          )}
        </Box>
      )}

      {body}

      {/* Create Project Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="new-project-customer">Customer</InputLabel>
              <Select
                labelId="new-project-customer"
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                label="Customer"
              >
                {state.customers?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              autoFocus
              label="Project name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={!formData.name.trim() || !formData.customer_id}
          >
            Create project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              label="Project name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditProject} variant="contained" disabled={!formData.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => { const p = selectedProject; handleMenuClose(); navigate(projectPath(p.id)); }}>
          <ListItemIcon><FolderOpenOutlined fontSize="small" /></ListItemIcon>
          Open
        </MenuItem>
        <MenuItem onClick={() => openEditDialog(selectedProject)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { const p = selectedProject; handleMenuClose(); setProjectToDelete(p); }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}><Delete fontSize="small" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={Boolean(projectToDelete)}
        title="Delete project?"
        message={`"${projectToDelete?.name || ""}" will be deleted for everyone in your workspace. This cannot be undone.`}
        confirmLabel="Delete project"
        onConfirm={handleConfirmDelete}
        onClose={() => setProjectToDelete(null)}
      />
    </PageContainer>
  );
});

ProjectList.displayName = "ProjectList";

export default ProjectList;
